<template>
  <div class="layer-item-wrapper">
    <!-- 大屏图层不可拖拽 -->
    <template v-if="isBigScreen">
      <div v-for="(item, index) in widgetPageInfo.list" :key="index">
        <div
          class="layer-item"
          :class="{ 'active-item': widgetStore.selectWidget.key === item.key }"
          @click="triggerWidget(item)"
        >
          <svg-icon :icon-class="getSvgIcon(item.imageSrc!)" class="svg-icon" />
          <span class="title">{{ item.title }}</span>
          <svg-icon
            icon-class="grid-row-setting-remove"
            class="delete"
            @click.stop="deleteWidget(item)"
          />
        </div>

        <!-- 面板 -->
        <div v-if="item.type === 'HHTabPaneWidget'">
          <div v-if="item.dataProp" class="children-item">
            <div
              v-for="(child, childIndex) in formatPaneWidgetChildren(item.dataProp)"
              :key="childIndex"
              class="layer-item-wrapper"
            >
              <LayerItem :widget-page-info="child" @trigger-item="triggerWidget" />
            </div>
          </div>
        </div>
        <!-- 其他 -->
        <div v-else>
          <div v-if="item.children" class="children-item">
            <div
              v-for="(child, childIndex) in item.children"
              :key="childIndex"
              class="layer-item-wrapper"
            >
              <LayerItem :widget-page-info="child" @trigger-item="triggerWidget" />
            </div>
          </div>
          <!-- 组合 -->
          <div v-if="item.groupComponents" class="children-item">
            <div
              v-for="(child, childIndex) in item.groupComponents"
              :key="childIndex"
              class="layer-item-wrapper"
            >
              <LayerItem :widget-page-info="getGroupChild(child)" @trigger-item="triggerWidget" />
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- 普通页面图层可拖拽 -->
    <template v-else>
      <draggable
        class="list-group"
        :list="widgetPageInfo.list"
        :group="{ name: 'widget' }"
        ghost-class="ghost"
        item-key="key"
        @change="draggableChange"
      >
        <template #item="{ element: item }">
          <div class="layer-item-box">
            <div
              class="layer-item"
              :class="{
                'active-item': widgetStore.selectWidget.key === item.key && comp?.key === item.key
              }"
              @click="triggerWidget(item)"
            >
              <svg-icon :icon-class="getSvgIcon(item.imageSrc!)" class="svg-icon" />
              <span class="title">{{ item.title }}</span>
              <svg-icon
                icon-class="grid-row-setting-remove"
                class="delete"
                @click.stop="deleteWidget(item)"
              />
            </div>
            <template v-if="comp?.key === item.key || (!comp && hasListComs.includes(item.type))">
              <div v-if="item.type === 'HHGrid'" class="children-item">
                <div
                  v-for="(child, childIndex) in item.children"
                  :key="childIndex"
                  class="layer-item-wrapper"
                >
                  <LayerItem :widget-page-info="child" @trigger-item="triggerWidget" />
                </div>
              </div>
              <div v-else-if="item.dataProp" class="children-item">
                <div
                  v-for="(child, childIndex) in formatPaneWidgetChildren(item.dataProp)"
                  :key="childIndex"
                  class="layer-item-wrapper"
                >
                  <LayerItem :widget-page-info="child" @trigger-item="triggerWidget" />
                </div>
              </div>
            </template>
          </div>
        </template>
      </draggable>
    </template>
  </div>

  <custom-dialog
    v-model="deleteDialogVisible"
    :content-text="deleteDialogText"
    @submit="deleteWidgetSubmit"
  />
</template>
<script setup lang="ts">
  import draggable from 'vuedraggable'
  import { useWidget, useProject } from '@haohan/clw-store'
  import { CustomDialog, EMITTER_NAME } from '@haohan/clw-platform-public'
  import { emitter } from '@haohan/clw-utils'
  import { cloneDeep } from 'lodash-es'

  const hasListComs = ['HHGrid', 'HHTabPaneWidget']

  const projectStore = useProject()
  const widgetStore = useWidget()
  interface Props {
    widgetPageInfo: WidgetPageInfo
    comp?: Widget | null
  }
  const props = withDefaults(defineProps<Props>(), {
    comp: null
  })
  const emit = defineEmits<{
    (e: 'triggerItem', item: Widget): void
  }>()
  const getSvgIcon = (src: string): string => {
    if (src && src.indexOf('.png') !== -1) {
      src = src.substring(0, src.indexOf('.png'))
    }
    return `tuceng-${src}`
  }
  const triggerWidget = (widget: Widget): void => {
    emit('triggerItem', widget)
  }

  const formatPaneWidgetChildren = (attributes: any) => {
    attributes.options ? attributes.options : []
  }

  // 删除
  const deleteDialogVisible = ref(false)
  const deleteDialogText = ref('')
  const deleteWidget = (widget: Widget) => {
    deleteDialogVisible.value = true
    deleteDialogText.value = `是否删除组件: ${widget.title}?`
    // 选中组件
    widgetStore.changeSelectWidget(widget)
  }
  const deleteWidgetSubmit = () => {
    nextTick(() => {
      // 删除组件
      if (widgetStore.selectWidget.key) {
        widgetStore.deleteSelectWidgetFromWidgetDataList()
        emitter.emit(EMITTER_NAME.RemoveWidget, cloneDeep(widgetStore.selectWidget))
        const Event = new MouseEvent('mousedown')
        document.dispatchEvent(Event)
      }
    })
  }

  // 增加组件处理
  const draggableChange = (evt: any) => {
    console.log('[ evt ]-36', evt)
    Object.keys(evt).forEach((e) => {
      if (e === 'added') {
        widgetStore.changeSelectWidget(evt[e].element)
      }
    })
  }

  // 是否为大屏
  const isBigScreen = computed(() => {
    if (projectStore.currentPage?.variables?.pageType === 'bigScreen') {
      return true
    }
    return false
  })

  // 大屏组件, 组合后展示出图层
  const getGroupChild = (child: any) => {
    // 组装 LayerItem 需要的数据格式
    return { list: [child] }
  }
</script>

<style lang="scss" scoped>
  .layer-item-wrapper {
    width: 100%;
    color: var(--text-color-primary);

    .layer-item {
      margin: 16px 0;
      height: 24px;
      // line-height: 24px;
      font-size: 14px;
      cursor: pointer;
      display: flex;
      align-items: center;

      &:hover {
        color: var(--text-color-focus);

        .delete {
          display: block;
          // transform-origin: center center;
          // transform: scale(1.2);
        }
      }

      .svg-icon {
        width: 16px;
        height: 16px;
        margin-right: 10px;
        vertical-align: middle;
      }

      .delete {
        display: none;
        margin-left: 10px;
        width: 10px;
        height: 10px;
      }

      &.active-item {
        color: var(--text-color-focus);
      }

      .title {
        white-space: nowrap;
      }
    }

    .children-item {
      padding-left: 10px;
    }
  }
</style>
